import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import {
  Autocomplete,
  AutocompleteInputChangeReason,
  CircularProgress,
  IconButton,
  TextField,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { Tooltip } from '@ui/Tooltip';
import { Search as SearchIcon } from '@mui/icons-material';
import { Coordinate } from '@shared/models/geography.model';
import { useDebouncedFunction } from '@client/hooks/use-utils.hooks';
import {
  DESIGN_COLLAPSED_TABS_INSET_VAR,
  DESIGN_RIGHT_PANEL_WIDTH_VAR,
} from '../design-right-panel-resize.hook';
import {
  applyLocationSearchSelection,
  type LocationSearchMap,
} from '../helpers/location-search-selection.helpers';

type GeocodingFeature = {
  id: string;
  place_name: string;
  center: [number, number]; // [lng, lat]
};

type MapLocationSearchProps = {
  map: LocationSearchMap | undefined;
  accessToken: string | undefined;
  proximity: Coordinate | undefined;
  onLocationSelect: (coordinate: Coordinate) => void | Promise<unknown>;
};

export const MapLocationSearch: FC<MapLocationSearchProps> = ({
  map,
  accessToken,
  proximity,
  onLocationSelect,
}) => {
  const { classes, cx } = useStyles();
  const containerRef = useRef<HTMLDivElement>(null);
  const highlightedRef = useRef<GeocodingFeature | null>(null);
  const inputValueRef = useRef('');
  const pendingSelectRef = useRef(false);
  const selectSequence = useRef(0);

  // Resolver set by handleKeyDown when waiting for a fetch to complete.
  // fetchSuggestions calls it once results are ready.
  const fetchResolverRef = useRef<
    ((features: GeocodingFeature[]) => void) | null
  >(null);

  // Track which query the current options were loaded for.
  const optionsQueryRef = useRef('');
  const optionsRef = useRef<GeocodingFeature[]>([]);

  const [inputValue, setInputValue] = useState('');
  const [optionsState, setOptionsState] = useState<GeocodingFeature[]>([]);
  const [loading, setLoading] = useState(false);
  const [focused, setFocused] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [suppressOpen, setSuppressOpen] = useState(false);

  const setOptions = useCallback(
    (query: string, features: GeocodingFeature[]) => {
      optionsRef.current = features;
      optionsQueryRef.current = query;
      setOptionsState(features);
    },
    [],
  );

  useEffect(() => {
    if (pendingSelectRef.current) {
      pendingSelectRef.current = false;
      containerRef.current?.querySelector('input')?.select();
    }
  }, [inputValue]);

  const selectAll = useCallback(() => {
    pendingSelectRef.current = true;
  }, []);

  const submitFeature = useCallback(
    (feature: GeocodingFeature) => {
      if (!map) return;
      const [lng, lat] = feature.center;
      const coordinate: Coordinate = { lat, lng };
      const sequence = ++selectSequence.current;
      void applyLocationSearchSelection({
        map,
        coordinate,
        select: onLocationSelect,
        isCurrent: () => sequence === selectSequence.current,
      });
      setInputValue(feature.place_name);
      inputValueRef.current = feature.place_name;
      setSuppressOpen(true);
      selectAll();
    },
    [map, onLocationSelect, selectAll],
  );

  const geocode = useCallback(
    async (query: string): Promise<GeocodingFeature[]> => {
      if (!accessToken || query.length < 2) return [];
      const proximityParam = proximity
        ? `&proximity=${proximity.lng},${proximity.lat}`
        : '';
      const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json?access_token=${accessToken}&autocomplete=true&types=place,address,poi&limit=5${proximityParam}`;
      const res = await fetch(url);
      const data = await res.json();
      return data.features ?? [];
    },
    [accessToken, proximity],
  );

  const fetchSuggestions = useCallback(
    async (query: string) => {
      setLoading(true);
      try {
        const features = await geocode(query);
        if (query === inputValueRef.current) {
          setOptions(query, features);
          // Resolve any handleKeyDown that is waiting for this fetch
          fetchResolverRef.current?.(features);
          fetchResolverRef.current = null;
        }
      } catch {
        setOptions(query, []);
        fetchResolverRef.current?.([]);
        fetchResolverRef.current = null;
      } finally {
        setLoading(false);
      }
    },
    [geocode, setOptions],
  );

  const debouncedFetch = useDebouncedFunction(fetchSuggestions, 300);

  const handleInputChange = useCallback(
    (
      _: React.SyntheticEvent,
      value: string,
      reason: AutocompleteInputChangeReason,
    ) => {
      if (reason === 'input') {
        // Cancel any pending submit-after-fetch; resolve so the await unblocks
        fetchResolverRef.current?.([]);
        fetchResolverRef.current = null;
        highlightedRef.current = null;
        setSuppressOpen(false);
        setInputValue(value);
        inputValueRef.current = value;
        debouncedFetch(value);
      } else if (reason === 'reset') {
        setInputValue(value);
        inputValueRef.current = value;
        selectAll();
      } else if (reason === 'clear') {
        setInputValue('');
        inputValueRef.current = '';
        setOptions('', []);
      }
    },
    [debouncedFetch, selectAll, setOptions],
  );

  const handleChange = useCallback(
    (_: React.SyntheticEvent, feature: GeocodingFeature | null) => {
      if (!feature) return;
      submitFeature(feature);
    },
    [submitFeature],
  );

  const handleHighlightChange = useCallback(
    (_: React.SyntheticEvent, option: GeocodingFeature | null) => {
      highlightedRef.current = option;
    },
    [],
  );

  /**
   * Submit the current query: the highlighted suggestion, else the first
   * result (fetching it first when the debounce has not fired yet).
   */
  const submitQuery = useCallback(async () => {
    const highlighted = highlightedRef.current;
    if (highlighted) {
      submitFeature(highlighted);
      return;
    }
    const query = inputValueRef.current;
    if (!query) return;

    // Options already loaded for this query: submit immediately
    if (optionsQueryRef.current === query && optionsRef.current.length > 0) {
      const first = optionsRef.current[0];
      if (first) submitFeature(first);
      return;
    }

    // Options not loaded yet: wait for the in-flight or upcoming fetch.
    // If the debounce hasn't fired yet, trigger an immediate fetch.
    const features = await new Promise<GeocodingFeature[]>((resolve) => {
      fetchResolverRef.current = resolve;
      fetchSuggestions(query);
    });

    if (query !== inputValueRef.current) return;
    const first = features[0];
    if (first) submitFeature(first);
  }, [fetchSuggestions, submitFeature]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      e.preventDefault();
      void submitQuery();
    },
    [submitQuery],
  );

  const handleOpenSearch = useCallback(() => {
    setExpanded(true);
    // Focus the input after the field has rendered
    requestAnimationFrame(() => {
      containerRef.current?.querySelector('input')?.focus();
    });
  }, []);

  /**
   * Closed: open the field. Open: search the typed query, or close the field
   * again when nothing has been typed.
   */
  const handleIconClick = useCallback(() => {
    if (!expanded) {
      handleOpenSearch();
      return;
    }
    if (inputValueRef.current) {
      void submitQuery();
      return;
    }
    setExpanded(false);
    containerRef.current?.querySelector('input')?.blur();
  }, [expanded, handleOpenSearch, submitQuery]);

  return (
    <div
      ref={containerRef}
      className={cx(classes.root, expanded && classes.rootExpanded)}
    >
      <Tooltip title={expanded ? '' : 'Search location'} placement="left">
        <IconButton
          className={cx(classes.iconButton, expanded && classes.iconButtonOver)}
          // Keep the input focused: its blur handler would close the field
          // before the click lands.
          onMouseDown={(event) => event.preventDefault()}
          onClick={handleIconClick}
          size="small"
          aria-label="Search location"
        >
          <SearchIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      {expanded && (
        <div
          onMouseDown={(e) => {
            const input = containerRef.current?.querySelector('input');
            if (!input || document.activeElement === input) return;
            // Prevent the browser from positioning the cursor on mouseup.
            // We focus and select manually instead.
            e.preventDefault();
            input.focus();
            input.select();
          }}
        >
          <Autocomplete<GeocodingFeature, false, false, false>
            options={optionsState}
            getOptionLabel={(o) => o.place_name}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            filterOptions={(x) => x}
            autoHighlight
            popupIcon={null}
            open={focused && inputValue.length >= 2 && !suppressOpen}
            loading={loading}
            inputValue={inputValue}
            onInputChange={handleInputChange}
            onChange={handleChange}
            onHighlightChange={handleHighlightChange}
            noOptionsText="No results"
            size="small"
            classes={{ paper: classes.paper }}
            renderInput={(params) => {
              // MUI v9 Autocomplete exposes slotProps; keep TextField on the same API.
              const { slotProps, ...restParams } = params;
              const muiKeyDown = slotProps.htmlInput.onKeyDown;
              return (
                <TextField
                  {...restParams}
                  placeholder="Search location"
                  variant="outlined"
                  onFocus={() => {
                    setFocused(true);
                    containerRef.current?.querySelector('input')?.select();
                  }}
                  onBlur={() => {
                    setFocused(false);
                    setExpanded(false);
                  }}
                  slotProps={{
                    inputLabel: slotProps.inputLabel,
                    input: {
                      ...slotProps.input,
                      endAdornment: loading ? (
                        <CircularProgress size={14} />
                      ) : (
                        slotProps.input.endAdornment
                      ),
                    },
                    htmlInput: {
                      ...slotProps.htmlInput,
                      onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
                        if (e.key === 'Enter') {
                          handleKeyDown(e);
                        } else {
                          muiKeyDown?.(e);
                        }
                      },
                    },
                  }}
                  sx={{
                    backgroundColor: 'background.paper',
                    borderRadius: 1,
                    width: '100%',
                    '& .MuiOutlinedInput-root': {
                      // Room for the search icon overlaying the right edge.
                      pr: '38px !important',
                    },
                  }}
                />
              );
            }}
          />
        </div>
      )}
    </div>
  );
};

const useStyles = makeStyles()((theme) => ({
  root: {
    // Sits left of the view-settings icon (right: 64), centred on the north mark.
    position: 'absolute',
    top: 30,
    right: `calc(100px + var(${DESIGN_RIGHT_PANEL_WIDTH_VAR}, 0px) + var(${DESIGN_COLLAPSED_TABS_INSET_VAR}, 0px))`,
    transform: 'translateY(-50%)',
    display: 'flex',
    alignItems: 'center',
    zIndex: 3,
  },
  rootExpanded: {
    // Starts at the middle of the map and ends at the north control's left.
    width: `calc(50% - 100px - var(${DESIGN_RIGHT_PANEL_WIDTH_VAR}, 0px) - var(${DESIGN_COLLAPSED_TABS_INSET_VAR}, 0px))`,
    '& > div': {
      width: '100%',
    },
  },
  iconButton: {
    backgroundColor: 'transparent',
    flexShrink: 0,
    // Nudged in from the field's right edge without moving the field itself.
    transform: 'translateX(-4px)',
    boxShadow: 'none',
    // Matches the north/view control: 70% black, full black on hover.
    color: 'rgba(0, 0, 0, 0.7)',
    transition: 'color 0.15s ease',
    '&:hover': {
      backgroundColor: 'transparent',
      color: 'rgba(0, 0, 0, 1)',
    },
  },
  /** Stays visible on top of the open field, at the same spot as when closed. */
  iconButtonOver: {
    position: 'absolute',
    right: 4,
    top: '50%',
    transform: 'translateY(-50%)',
    zIndex: 1,
  },
  paper: {
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[4],
  },
}));
